import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Shield } from 'lucide-react';
import { footerLinks } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Newsletter Section */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={springConfig}
        className="border-b border-gray-800"
        style={{ paddingTop: '40px', paddingBottom: '40px' }}
      >
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h3 
              className="text-3xl font-semibold text-white mb-4"
            >
              Stay Updated
            </h3>
            <p className="text-gray-400 mb-8">
              Get the latest features, tips, and exclusive offers delivered to your inbox.
            </p>
            <div className="relative max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-6 py-4 pr-36 rounded-full bg-gray-800 border border-gray-700 focus:border-orange-400 focus:outline-none text-white"
              />
              <button
                className="absolute right-2 top-2 bottom-2 px-6 rounded-full text-white font-medium"
                style={{
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)'
                }}
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Footer Content */}
      <div className="container py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_509fd741-eab5-4a3e-b38c-f820fa1614e9/artifacts/y9uyjr7q_256-01.png" 
                alt="TypeIt4U Logo"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-white">Typeit 4U</span>
            </Link>
            <p className="text-gray-400 mb-6 max-w-xs">
              AI-powered keyboard built for Indian users. Transform your typing experience with intelligent features.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/typeit4u/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 flex items-center justify-center transition-all"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.youtube.com/@typeit4ukeyboard"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-red-600 flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors flex items-start gap-2"
                  >
                    {link.name}
                    {link.highlight && (
                      <span className="flex items-center gap-1 text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                        <Shield className="w-3 h-3" />
                        {link.highlight}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('http') ? (
                    <a 
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            
            {/* Resources */}
            <h4 className="text-white font-semibold mb-4 mt-8">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources?.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 pt-12 border-t border-gray-800 mb-8">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-orange-400" />
            <span className="text-sm">support@deyvainfotech.com</span>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-orange-400" />
            <span className="text-sm">+91 80564 88828</span>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-orange-400" />
            <span className="text-sm">Chennai, India</span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © {currentYear} TypeIt4U. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Shield className="w-4 h-4 text-green-500" />
              <span>100% Secure • No Keystroke Logging • Privacy Protected</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
